import type { MessageKey, Translator } from "~/i18n";
import { translate } from "~/i18n";

export type SettingsPath =
  | "/settings/general"
  | "/settings/organization"
  | "/settings/connections"
  | "/settings/comms"
  | "/settings/triggers"
  | "/settings/providers"
  | "/settings/appearance"
  // MorrowOS: hidden coding-tool setting — Source Control removed from Settings.
  // | "/settings/source-control"
  | "/settings/packages"
  | "/settings/billing"
  | "/settings/archived"
  | "/settings/support";

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly to: SettingsPath;
  readonly targetId?: string;
}

/**
 * Section labels in sidebar order. The sidebar nav and the search-result
 * subtitles both render from this record, so each label exists once.
 */
export const SETTINGS_SECTION_LABELS: Readonly<Record<SettingsPath, string>> = {
  "/settings/organization": "Organization",
  "/settings/general": "General",
  "/settings/connections": "Connections",
  "/settings/comms": "Comms",
  "/settings/triggers": "Triggers",
  "/settings/providers": "Providers",
  "/settings/appearance": "Appearance",
  // MorrowOS: hidden coding-tool setting — Source Control removed from Settings.
  // "/settings/source-control": "Source Control",
  "/settings/packages": "Packages",
  "/settings/billing": "Billing",
  "/settings/archived": "Archive",
  "/settings/support": "Support",
};

const SETTINGS_SECTION_TITLE_KEYS: Readonly<Record<SettingsPath, MessageKey>> = {
  "/settings/organization": "settings.section.organization",
  "/settings/general": "settings.section.general",
  "/settings/connections": "settings.section.connections",
  "/settings/comms": "settings.section.comms",
  "/settings/triggers": "settings.section.triggers",
  "/settings/providers": "settings.section.providers",
  "/settings/appearance": "settings.section.appearance",
  "/settings/packages": "settings.section.packages",
  "/settings/billing": "settings.section.billing",
  "/settings/archived": "settings.section.archive",
  "/settings/support": "settings.section.support",
};

export function settingsSectionLabel(path: SettingsPath, t?: Translator): string {
  return t ? t(SETTINGS_SECTION_TITLE_KEYS[path]) : SETTINGS_SECTION_LABELS[path];
}

/**
 * Every searchable setting, in result order. This catalog is the single
 * source of truth for anchor ids and visible titles: panels render both via
 * `searchableSetting`, so a retitle (or, later, a translation pass) happens
 * here once instead of separately in the panel and the index.
 */
export const SETTINGS_SEARCH_ITEMS = [
  {
    id: "color-scheme",
    title: "Color scheme",
    to: "/settings/appearance",
    // The scheme tiles sit at the top of the Appearance section.
    targetId: "appearance",
  },
  {
    id: "theme",
    title: "Themes",
    to: "/settings/appearance",
    // Theme cards live directly under the scheme tiles; the section is the
    // stable scroll destination for both.
    targetId: "appearance",
  },
  {
    // Prefixed because the slider control already owns the `glass-opacity` id.
    id: "setting-glass-opacity",
    title: "Glass opacity",
    to: "/settings/appearance",
  },
  {
    id: "environment-identification",
    title: "Environment identification",
    to: "/settings/appearance",
    // The setting is stage-dependent, so its parent section is the stable destination.
    targetId: "appearance",
  },
  {
    id: "interface-font",
    title: "Interface font",
    to: "/settings/appearance",
  },
  {
    id: "prompt-font",
    title: "Prompt font",
    to: "/settings/appearance",
  },
  {
    id: "font-smoothing",
    title: "Font smoothing",
    to: "/settings/appearance",
  },
  // MorrowOS: hidden coding-tool setting — project (repository) grouping.
  // {
  //   id: "project-grouping",
  //   title: "Project grouping",
  //   to: "/settings/general",
  // },
  // MorrowOS: hidden coding-tool setting — auto-settle inactive/merged threads.
  // {
  //   id: "auto-settle-inactive-threads",
  //   title: "Auto-settle inactive threads",
  //   to: "/settings/general",
  // },
  // {
  //   id: "auto-settle-merged-threads",
  //   title: "Auto-settle merged threads",
  //   to: "/settings/general",
  // },
  {
    id: "team-profile",
    title: "Portrait",
    to: "/settings/general",
    targetId: "account",
  },
  {
    id: "ui-language",
    title: "Language",
    to: "/settings/general",
  },
  {
    id: "time-format",
    title: "Time format",
    to: "/settings/general",
  },
  // MorrowOS: hidden coding-tool setting — hide whitespace (diff-only).
  // {
  //   id: "hide-whitespace-changes",
  //   title: "Hide whitespace changes",
  //   to: "/settings/general",
  // },
  // MorrowOS: hidden coding-tool setting — provider update checks.
  // {
  //   id: "provider-update-checks",
  //   title: "Provider update checks",
  //   to: "/settings/general",
  // },
  // MorrowOS: hidden coding-tool setting — new-thread worktree mode + start-from-origin.
  // {
  //   id: "new-threads",
  //   title: "New threads",
  //   to: "/settings/general",
  // },
  // {
  //   id: "start-from-origin",
  //   title: "Start from origin",
  //   to: "/settings/general",
  //   targetId: "new-threads",
  // },
  // MorrowOS: hidden coding-tool setting — add-project start location.
  // {
  //   id: "add-project-starts-in",
  //   title: "Add project starts in",
  //   to: "/settings/general",
  // },
  {
    id: "archive-confirmation",
    title: "Archive confirmation",
    to: "/settings/general",
  },
  {
    id: "delete-confirmation",
    title: "Delete confirmation",
    to: "/settings/general",
  },
  {
    id: "text-generation-model",
    title: "Text generation model",
    to: "/settings/general",
  },
  {
    id: "diagnostics",
    title: "Diagnostics",
    to: "/settings/general",
  },
  // MorrowOS: hidden coding-tool setting — legacy plan mode / token streaming / sidebar.
  // {
  //   id: "legacy-plan-mode",
  //   title: "Plan mode (legacy)",
  //   to: "/settings/general",
  // },
  // {
  //   id: "legacy-token-streaming",
  //   title: "Stream token by token (legacy)",
  //   to: "/settings/general",
  // },
  // {
  //   id: "legacy-sidebar",
  //   title: "Sidebar (legacy)",
  //   to: "/settings/general",
  // },
  {
    id: "providers",
    title: "Providers",
    to: "/settings/providers",
  },
  // MorrowOS: hidden coding-tool setting — Source Control removed from Settings.
  // {
  //   id: "source-control",
  //   title: "Source control",
  //   to: "/settings/source-control",
  // },
  {
    id: "remote-environments",
    title: "Remote environments",
    to: "/settings/connections",
  },
  {
    id: "organization",
    title: "Organization",
    to: "/settings/organization",
  },
  {
    id: "organization-members",
    title: "Members and roles",
    to: "/settings/organization",
  },
  {
    id: "packages",
    title: "Packages",
    to: "/settings/packages",
  },
  {
    id: "packages-addons",
    title: "Add-ons & credit funds",
    to: "/settings/packages",
  },
  {
    id: "archive",
    title: "Archived threads",
    to: "/settings/archived",
  },
  {
    id: "help-center",
    title: "Help Center",
    to: "/settings/support",
  },
  {
    id: "submit-request",
    title: "Submit a request",
    to: "/settings/support",
  },
  {
    id: "system-status",
    title: "System status",
    to: "/settings/support",
  },
  {
    id: "support-setup",
    title: "Setup & getting started",
    to: "/settings/support",
  },
  {
    id: "support-integrations",
    title: "Integrations",
    to: "/settings/support",
  },
  {
    id: "support-memory",
    title: "Company memory",
    to: "/settings/support",
  },
  {
    id: "support-security",
    title: "Security & privacy",
    to: "/settings/support",
  },
  {
    id: "support-billing",
    title: "Billing & plans",
    to: "/settings/support",
  },
  {
    id: "support-faq",
    title: "FAQ",
    to: "/settings/support",
  },
  {
    id: "support-email",
    title: "General support",
    to: "/settings/support",
    targetId: "support-contact",
  },
  {
    id: "support-security-email",
    title: "Security contact",
    to: "/settings/support",
    targetId: "support-contact",
  },
  {
    id: "support-legal-email",
    title: "Legal contact",
    to: "/settings/support",
    targetId: "support-contact",
  },
  {
    id: "support-phone",
    title: "Phone",
    to: "/settings/support",
    targetId: "support-contact",
  },
  {
    id: "support-book-call",
    title: "Book a call",
    to: "/settings/support",
  },
  {
    id: "privacy-policy",
    title: "Privacy Policy",
    to: "/settings/support",
  },
  {
    id: "terms-of-service",
    title: "Terms of Service",
    to: "/settings/support",
  },
  {
    id: "dpa",
    title: "Data Processing Addendum",
    to: "/settings/support",
  },
  {
    id: "cookie-policy",
    title: "Cookie Policy",
    to: "/settings/support",
  },
  {
    id: "trust-center",
    title: "Trust Center",
    to: "/settings/support",
  },
  {
    id: "legal-company",
    title: "Enortic, Inc.",
    to: "/settings/support",
    targetId: "legal-company",
  },
] as const satisfies ReadonlyArray<SettingsSearchItem>;

export type SettingsSearchItemId = (typeof SETTINGS_SEARCH_ITEMS)[number]["id"];

const SEARCH_ITEMS_BY_ID = Object.fromEntries(
  SETTINGS_SEARCH_ITEMS.map((item) => [item.id, item]),
) as Readonly<Record<SettingsSearchItemId, SettingsSearchItem>>;

const SETTINGS_SEARCH_TITLE_KEYS: Readonly<Record<SettingsSearchItemId, MessageKey>> = {
  "color-scheme": "settings.search.colorScheme",
  theme: "settings.search.theme",
  "setting-glass-opacity": "settings.search.glassOpacity",
  "environment-identification": "settings.search.environmentIdentification",
  "interface-font": "settings.search.interfaceFont",
  "prompt-font": "settings.search.promptFont",
  "font-smoothing": "settings.search.fontSmoothing",
  "team-profile": "profile.avatar.title",
  "ui-language": "settings.search.language",
  "time-format": "settings.search.timeFormat",
  "archive-confirmation": "settings.search.archiveConfirmation",
  "delete-confirmation": "settings.search.deleteConfirmation",
  "text-generation-model": "settings.search.textGenerationModel",
  diagnostics: "settings.search.diagnostics",
  providers: "settings.search.providers",
  "remote-environments": "settings.search.remoteEnvironments",
  organization: "settings.search.organization",
  "organization-members": "settings.search.organizationMembers",
  packages: "settings.search.packages",
  "packages-addons": "settings.search.packagesAddons",
  archive: "settings.search.archive",
  "help-center": "settings.search.helpCenter",
  "submit-request": "settings.search.submitRequest",
  "system-status": "settings.search.systemStatus",
  "support-setup": "settings.search.supportSetup",
  "support-integrations": "settings.search.supportIntegrations",
  "support-memory": "settings.search.supportMemory",
  "support-security": "settings.search.supportSecurity",
  "support-billing": "settings.search.supportBilling",
  "support-faq": "settings.search.supportFaq",
  "support-email": "settings.search.supportEmail",
  "support-security-email": "settings.search.supportSecurityEmail",
  "support-legal-email": "settings.search.supportLegalEmail",
  "support-phone": "settings.search.supportPhone",
  "support-book-call": "settings.search.supportBookCall",
  "privacy-policy": "settings.search.privacyPolicy",
  "terms-of-service": "settings.search.termsOfService",
  dpa: "settings.search.dpa",
  "cookie-policy": "settings.search.cookiePolicy",
  "trust-center": "settings.search.trustCenter",
  "legal-company": "settings.search.legalCompany",
};

function localizedSettingTitle(id: string, t?: Translator): string | null {
  if (!(id in SETTINGS_SEARCH_TITLE_KEYS)) return null;
  const key = SETTINGS_SEARCH_TITLE_KEYS[id as SettingsSearchItemId];
  return t ? t(key) : null;
}

/**
 * `id` and `title` props for the element a search item anchors to. Panels
 * spread (or pick from) this instead of restating the strings, so the catalog
 * and the rendered settings cannot drift apart.
 */
export function searchableSetting(
  id: SettingsSearchItemId,
  t?: Translator,
): {
  readonly id: string;
  readonly title: string;
} {
  const { id: anchorId, title } = SEARCH_ITEMS_BY_ID[id];
  return { id: anchorId, title: localizedSettingTitle(id, t) ?? title };
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function settingSearchHaystack(item: SettingsSearchItem): string {
  const key =
    item.id in SETTINGS_SEARCH_TITLE_KEYS
      ? SETTINGS_SEARCH_TITLE_KEYS[item.id as SettingsSearchItemId]
      : null;
  const localized = key ? [translate("en", key), translate("tr", key)] : [];
  return [item.title, ...localized].join(" ");
}

export function searchSettings(
  query: string,
  items: ReadonlyArray<SettingsSearchItem> = SETTINGS_SEARCH_ITEMS,
  t?: Translator,
): ReadonlyArray<SettingsSearchItem> {
  const catalog = items ?? SETTINGS_SEARCH_ITEMS;
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  return catalog
    .filter((item) => normalizeSearchText(settingSearchHaystack(item)).includes(normalizedQuery))
    .map((item) => {
      const title = localizedSettingTitle(item.id, t);
      return title ? { ...item, title } : item;
    });
}
